import type { PostContent } from '../api/createPost'
import { FACEBOOK_RESTRICTIONS } from './restrictions'

const ALLOWED_IMAGE_FORMATS = FACEBOOK_RESTRICTIONS.image.allowedFormats as readonly string[]
const ALLOWED_VIDEO_FORMATS = FACEBOOK_RESTRICTIONS.video.allowedFormats as readonly string[]

const rejectionReason = (type: string) => {
  if (type.startsWith('image/')) {
    return `Facebook does not accept ${type} images. Please convert to JPEG or PNG.`
  }
  if (type.startsWith('video/')) {
    return `Facebook does not accept ${type} videos. Please convert to MP4 or MOV.`
  }
  return `Facebook does not accept files of type ${type}. Only images and videos are supported.`
}

/**
 * Build a Facebook PostContent from a list of workspace media files plus a
 * caption. Mirrors the shape of the other platforms' postContent helpers.
 *
 * @param isStory When true, single-image/single-video posts are routed to
 *                photoStory/videoStory variants instead of image/video. Note
 *                that captions are silently dropped by Meta for stories;
 *                we still pass the value through but the API ignores it.
 *                Multi-photo and text-only modes are not supported for
 *                stories — they'll be marked invalid.
 * @param isReel  When true, single-video posts are routed to the reel
 *                variant instead of the feed video variant. Reels go through
 *                the /video_reels endpoint with a 3-phase resumable upload
 *                and unlock the cover-thumbnail picker. Mutually exclusive
 *                with isStory; reel mode requires a single video file
 *                (single image / multi-photo / text-only with reel enabled
 *                will be marked invalid).
 */
export const postContent = (
  uploadUrls: { url: string; file: { type: string } }[],
  caption: string,
  isStory: boolean = false,
  isReel: boolean = false,
): PostContent | { type: 'invalid'; reason: string } => {
  if (isStory && isReel) {
    return {
      type: 'invalid',
      reason: 'Facebook posts cannot be both a Story and a Reel — pick one',
    }
  }
  // Validate caption length
  if (caption && caption.length > FACEBOOK_RESTRICTIONS.caption.maxLength) {
    return {
      type: 'invalid',
      reason: `Caption too long (${caption.length}/${FACEBOOK_RESTRICTIONS.caption.maxLength} characters)`,
    }
  }

  if (uploadUrls.length === 0) {
    if (isStory) {
      return {
        type: 'invalid',
        reason: 'A Facebook story requires a photo or video',
      }
    }
    if (isReel) {
      return {
        type: 'invalid',
        reason: 'A Facebook reel requires a video',
      }
    }
    // Facebook allows text-only feed posts, but the /feed endpoint rejects
    // requests with no `message`, no `link`, and no `attached_media` — so an
    // empty caption + no media must be flagged as invalid here rather than
    // 400ing at the API edge with a confusing error.
    if (!caption.trim()) {
      return {
        type: 'invalid',
        reason: 'A Facebook text post requires a caption',
      }
    }
    return { type: 'text', caption }
  }

  if (uploadUrls.length === 1) {
    const fileType = uploadUrls[0]!.file.type
    if (ALLOWED_IMAGE_FORMATS.includes(fileType)) {
      if (isReel) {
        return {
          type: 'invalid',
          reason: 'Facebook reels require a video, not an image',
        }
      }
      return {
        type: isStory ? 'photoStory' : 'image',
        url: uploadUrls[0]!.url,
        caption,
      }
    }
    if (ALLOWED_VIDEO_FORMATS.includes(fileType)) {
      if (isReel) {
        return {
          type: 'reel',
          url: uploadUrls[0]!.url,
          caption,
        }
      }
      return {
        type: isStory ? 'videoStory' : 'video',
        url: uploadUrls[0]!.url,
        caption,
      }
    }
    return { type: 'invalid', reason: rejectionReason(fileType) }
  }

  // Stories don't support multi-photo
  if (isStory) {
    return {
      type: 'invalid',
      reason: 'Facebook stories only support a single photo or video',
    }
  }

  // Reels don't support multi-photo either
  if (isReel) {
    return {
      type: 'invalid',
      reason: 'Facebook reels only support a single video',
    }
  }

  // Multi-photo: must all be images
  if (!uploadUrls.every(({ file }) => ALLOWED_IMAGE_FORMATS.includes(file.type))) {
    return { type: 'invalid', reason: 'Facebook multi-photo posts can only contain images' }
  }

  if (uploadUrls.length > FACEBOOK_RESTRICTIONS.multiPhoto.maxItems) {
    return {
      type: 'invalid',
      reason: `Too many photos (${uploadUrls.length}/${FACEBOOK_RESTRICTIONS.multiPhoto.maxItems} max)`,
    }
  }

  return {
    type: 'multiPhoto',
    urls: uploadUrls.map(({ url }) => url),
    caption,
  }
}
